/**
 * Quick Actions.
 *
 * One-shot AI transforms run on selected text — "Summarize," "Rewrite,"
 * etc. Reuses the existing provider-agnostic `AIProvider.streamChat` (a
 * single synthetic user message, no conversation/persistence involved) and
 * the same trailing-throttle as regular chat streaming, so results still
 * feel live in the result popover.
 */
import { getActiveProvider } from "@/services/ai/providers";
import { createTrailingThrottle } from "@/services/ai/streaming/streamThrottle";
import { DEFAULT_CHAT_PARAMS, type ChatMessage } from "@/types";

export type QuickActionKind =
  | "summarize"
  | "rewrite"
  | "translate"
  | "explain-code"
  | "generate-email"
  | "convert-markdown"
  | "extract-actions";

export const QUICK_ACTION_LABEL: Record<QuickActionKind, string> = {
  summarize: "Summarize",
  rewrite: "Rewrite",
  translate: "Translate",
  "explain-code": "Explain code",
  "generate-email": "Generate email",
  "convert-markdown": "Convert to Markdown",
  "extract-actions": "Extract action items",
};

export const QUICK_ACTION_PROMPT: Record<QuickActionKind, (text: string) => string> = {
  summarize: (text) => `Summarize the following text concisely, in a few sentences:\n\n${text}`,
  rewrite: (text) => `Rewrite the following text to improve clarity and flow, keeping the same meaning:\n\n${text}`,
  translate: (text) =>
    `Translate the following text into English. If it is already in English, translate it into Spanish instead:\n\n${text}`,
  "explain-code": (text) => `Explain what the following code does, step by step:\n\n${text}`,
  "generate-email": (text) => `Turn the following notes into a polished, professional email:\n\n${text}`,
  "convert-markdown": (text) => `Convert the following text into well-formatted Markdown:\n\n${text}`,
  "extract-actions": (text) => `Extract a bullet list of action items from the following text:\n\n${text}`,
};

export interface RunQuickActionOptions {
  baseUrl: string;
  model: string;
  onDelta?: (fullText: string) => void;
  signal?: AbortSignal;
}

/** Runs a Quick Action to completion, returning the full result. `onDelta`
 * (throttled, matching `chatService`'s cadence) fires with the accumulated
 * text so far, for a result popover that fills in live rather than
 * appearing all at once. */
export async function runQuickAction(
  kind: QuickActionKind,
  selectedText: string,
  { baseUrl, model, onDelta, signal }: RunQuickActionOptions,
): Promise<string> {
  const message: ChatMessage = {
    id: "quick-action",
    role: "user",
    content: QUICK_ACTION_PROMPT[kind](selectedText),
    createdAt: Date.now(),
    status: "complete",
  };

  let full = "";
  const throttle = onDelta ? createTrailingThrottle(40, () => onDelta(full)) : null;
  for await (const delta of getActiveProvider().streamChat({
    baseUrl,
    model,
    messages: [message],
    params: DEFAULT_CHAT_PARAMS,
    signal,
  })) {
    full += delta.content;
    throttle?.trigger();
  }
  throttle?.flushNow();
  return full;
}
